import React from 'react';

const cardStyle = {
    backgroundColor: '#2d2d2d',
    border: '1px solid #404040',
    borderRadius: '8px',
    padding: '16px',
    boxShadow: '0 8px 20px rgba(0,0,0,0.15)'
};

const sectionTitleStyle = {
    marginBottom: '15px',
    color: '#e0e0e0',
    fontWeight: '600'
};

const labelStyle = {
    fontSize: '12px',
    color: '#a0a0a0',
    textTransform: 'uppercase',
    marginBottom: '6px',
    letterSpacing: '0.4px',
    fontWeight: '600'
};

const valueStyle = {
    fontSize: '18px',
    fontWeight: '600',
    color: '#f5f5f5'
};

const ColorSwatch = ({ color, label }) => (
    <div style={{
        backgroundColor: '#1f1f1f',
        borderRadius: '8px',
        padding: '12px',
        border: '1px solid #2f2f2f',
        display: 'flex',
        flexDirection: 'column',
        gap: '8px'
    }}>
        <div style={{
            width: '100%',
            height: '60px',
            borderRadius: '6px',
            background: color,
            border: '2px solid #3a3a3a',
            boxShadow: '0 2px 8px rgba(0,0,0,0.3)'
        }} />
        <div>
            <div style={{ ...labelStyle, marginBottom: '4px' }}>{label}</div>
            <div style={{ color: '#f5f5f5', fontSize: '13px', fontWeight: 600, wordBreak: 'break-all' }}>
                {color}
            </div>
        </div>
    </div>
);

const ColorPaletteSection = ({ colorPalette }) => {
    if (!colorPalette) return null;

    const { primary, secondary, background, text, accent_colors = [], semantic_colors = {} } = colorPalette;

    return (
        <div style={cardStyle}>
            <h5 style={{ margin: '0 0 16px', color: '#e0e0e0', fontWeight: 600 }}>Color Palette</h5>
            <div style={{ display: 'grid', gap: '16px' }}>
                {/* Primary Colors */}
                <div>
                    <div style={labelStyle}>Primary Colors</div>
                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
                        gap: '12px'
                    }}>
                        {primary && <ColorSwatch color={primary} label="Primary" />}
                        {secondary && <ColorSwatch color={secondary} label="Secondary" />}
                        {background && <ColorSwatch color={background} label="Background" />}
                        {text && <ColorSwatch color={text} label="Text" />}
                    </div>
                </div>

                {/* Accent Colors */}
                {accent_colors.length > 0 && (
                    <div>
                        <div style={labelStyle}>Accent Colors</div>
                        <div style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
                            gap: '12px'
                        }}>
                            {accent_colors.map((color, index) => (
                                <ColorSwatch key={index} color={color} label={`Accent ${index + 1}`} />
                            ))}
                        </div>
                    </div>
                )}

                {/* Semantic Colors */}
                {Object.keys(semantic_colors).length > 0 && (
                    <div>
                        <div style={labelStyle}>Semantic Colors</div>
                        <div style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
                            gap: '12px'
                        }}>
                            {Object.entries(semantic_colors).map(([key, color]) => (
                                <ColorSwatch key={key} color={color} label={key.charAt(0).toUpperCase() + key.slice(1)} />
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

const TypographySection = ({ typography }) => {
    if (!typography) return null;

    const { primary_font, secondary_font } = typography;

    if (!primary_font && !secondary_font) return null;

    return (
        <div style={cardStyle}>
            <h5 style={{ margin: '0 0 16px', color: '#e0e0e0', fontWeight: 600 }}>Typography</h5>
            <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                gap: '16px'
            }}>
                {primary_font && (
                    <div>
                        <div style={labelStyle}>Primary Font</div>
                        <div style={{
                            fontSize: '24px',
                            fontWeight: 600,
                            color: '#f5f5f5',
                            fontFamily: primary_font,
                            marginTop: '8px'
                        }}>
                            {primary_font}
                        </div>
                    </div>
                )}
                {secondary_font && (
                    <div>
                        <div style={labelStyle}>Secondary Font</div>
                        <div style={{
                            fontSize: '20px',
                            fontWeight: 500,
                            color: '#e5e5e5',
                            fontFamily: secondary_font,
                            marginTop: '8px'
                        }}>
                            {secondary_font}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

const DesignSemanticSection = ({ designSemantic }) => {
    if (!designSemantic || !designSemantic.result) return null;

    const { color_palette, typography, design_style, brand_notes } = designSemantic.result;

    const hasContent = color_palette || typography || design_style || brand_notes;
    if (!hasContent) return null;

    return (
        <div style={{ marginBottom: '30px' }}>
            <div style={{ display: 'grid', gap: '16px' }}>
                {/* Design Style */}
                {design_style && (
                    <div style={cardStyle}>
                        <div style={labelStyle}>Design Style</div>
                        <div style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            backgroundColor: '#6366f1',
                            color: '#fff',
                            padding: '8px 16px',
                            borderRadius: '999px',
                            fontSize: '16px',
                            fontWeight: 600,
                            marginTop: '8px'
                        }}>
                            {design_style}
                        </div>
                    </div>
                )}

                {/* Color Palette */}
                <ColorPaletteSection colorPalette={color_palette} />

                {/* Typography */}
                <TypographySection typography={typography} />

                {/* Brand Notes */}
                {brand_notes && (
                    <div style={{
                        ...cardStyle,
                        backgroundColor: '#252525',
                        border: '1px solid #333333'
                    }}>
                        <h5 style={{ margin: '0 0 12px', color: '#e0e0e0', fontWeight: 600 }}>Brand Notes</h5>
                        <p style={{
                            margin: 0,
                            color: '#d0d0d0',
                            lineHeight: '1.7',
                            fontSize: '15px',
                            whiteSpace: 'pre-wrap'
                        }}>
                            {brand_notes}
                        </p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default DesignSemanticSection;


